import React from 'react'
import type { CollapseProps, CollapsePanelProps } from 'antd/es/collapse'

import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion'
import { RightOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

/**
 * The shadcn half of the `../Collapse` seam: antd's `CollapseProps` rendered
 * on Radix's Accordion. Reached only when the design toggle says `shadcn`.
 *
 * `update-design`'s `src/antd/Collapse.tsx` is the reference this started
 * from; four things it does are wrong for THIS branch and are corrected here.
 *
 * 1. It replaces antd outright, so it is a plain component. Here it is the
 *    shadcn HALF of a dispatcher and may not be reached with the toggle off.
 * 2. It renders an empty `collapse-expand-icon` wrapper even when the call
 *    site's `expandIcon` returns nothing. Two of the six call sites that
 *    override the icon pass `() => null` precisely to remove it
 *    (`antdform-ui/FormListUI`, `forms/route-scheme/…/WeekDayCollapsibleList`),
 *    and rc-collapse's Panel drops the wrapper with it. The wrapper carries
 *    the icon's own inline padding, so keeping it shifts those two headers.
 * 3. It has NO MOTION. antd animates the panel height
 *    (`initCollapseMotion`, 0.2s), so snapping open is a behaviour change
 *    dressed as a repaint — see `useSettledClosed` below for how the exit
 *    animation and antd's `display: none` resting state are reconciled.
 * 4. It re-derives antd's geometry as shadcn defaults (`py-4`, `text-sm`).
 *    Roughly fifteen stylesheets in this repo override antd's header and
 *    content padding by name, so the metrics are transcribed from
 *    `antd/es/collapse/style` per `size` instead of re-invented.
 *
 * ---------------------------------------------------------------------------
 * DOM CONTRACT. `.ant-collapse-*` is deliberately NOT re-emitted — antd's own
 * Collapse stylesheet is unlayered and already on the page, so it would
 * outrank every utility here (AGENTS.md, "the reference re-emits `.ant-*`
 * freely"). Each part carries a `data-slot` instead, and every live stylesheet
 * that reached into antd's names grew a `[data-slot='…']` selector beside it:
 *
 *   data-slot="collapse"              root              (was `.ant-collapse`)
 *   data-slot="collapse-item"         one panel         (`.ant-collapse-item`)
 *   data-slot="collapse-header"       header row        (`-header`)
 *   data-slot="collapse-header-text"  the label         (`-header-text`)
 *   data-slot="collapse-expand-icon"  icon wrapper      (`-expand-icon`)
 *   data-slot="collapse-arrow"        the arrow, default or a
 *                                     call site's own      (`-arrow`)
 *   data-slot="collapse-extra"        right-side node   (`-extra`)
 *   data-slot="collapse-content"      content wrapper   (`-content`)
 *   data-slot="collapse-content-box"  content padding   (`-content-box`)
 *
 * plus `data-ghost` / `data-bordered` / `data-size` / `data-icon-position` on
 * the root — the last replacing `.ant-collapse-icon-position-{start,end}` —
 * and Radix's own `data-state="open|closed"` replacing
 * `.ant-collapse-item-active`.
 *
 * ---------------------------------------------------------------------------
 * TWO DIVERGENCES FROM antd, BOTH FORCED, NEITHER SILENT
 *
 * • THE TRIGGER IS A REAL `<button>`, so `extra` sits BESIDE it rather than
 *   inside the clickable area. antd's header is a `<div role="button">` and
 *   `extra` lives inside it, which is why `ConfigurationTab`'s edit affordance
 *   has to `stopPropagation()`; a `<button>` inside a `<button>` is invalid
 *   markup and the inner one would never receive its own click. Consequence:
 *   clicking `extra` no longer toggles the panel (the `stopPropagation` call
 *   stays correct, just redundant), and with `expandIconPosition="end"` the
 *   arrow renders BEFORE `extra` where antd puts it after — the arrow is
 *   inside the trigger and `extra` is not, so no `order` can cross them. One
 *   call site has both (`ConfigurationTab`'s Compliance panel).
 *
 * • ARROW KEYS MOVE BETWEEN HEADERS. Radix's roving focus is part of the
 *   Accordion; antd's Collapse has no keyboard model beyond Tab and Enter.
 *   Additive, so nothing that worked stops working.
 */

type PanelLike = {
  key?: React.Key
  /** `items`' name for the header. */
  label?: React.ReactNode
  /** `Collapse.Panel`'s name for the same thing. */
  header?: React.ReactNode
  children?: React.ReactNode
  extra?: React.ReactNode
  showArrow?: boolean
  forceRender?: boolean
  collapsible?: CollapseProps['collapsible']
  className?: string
  style?: React.CSSProperties
  headerClass?: string
  [prop: string]: unknown
}

/**
 * rc-collapse's `resetProps`: a panel's own props minus the ones the Collapse
 * itself consumes are spread onto the panel ELEMENT, so `data-*`, `id`,
 * `aria-*` and `onClick` reach the DOM. Five call sites select on such an
 * attribute (`keys/ContactKeysTab` puts a `data-testid` on a
 * `<Collapse.Panel>`), and dropping it here would silently break them in one
 * mode only. `childrenToPanels` spreads `element.props` into the same shape,
 * so the exclusion covers the legacy children API too.
 */
const CONSUMED_PANEL_PROPS = new Set([
  'key',
  'label',
  'header',
  'children',
  'extra',
  'showArrow',
  'forceRender',
  'collapsible',
  'className',
  'style',
  'headerClass',
  'onItemClick',
  'destroyInactivePanel',
])

const panelRestProps = (panel: PanelLike): Record<string, unknown> => {
  const rest: Record<string, unknown> = {}
  Object.keys(panel).forEach((name) => {
    if (!CONSUMED_PANEL_PROPS.has(name)) rest[name] = panel[name]
  })
  return rest
}

/**
 * rc-collapse's `getActiveKeysArray`: a scalar becomes a one-element array and
 * every key is stringified, which is what makes `key={0}` and `activeKey={[0]}`
 * meet. `onChange` then hands an ARRAY back even in `accordion` mode — a
 * scalar would break every `onChange={(keys) => Array.isArray(keys) ? … : …}`
 * call site, and ten of them are written exactly that way.
 */
const toKeyArray = (
  value: CollapseProps['activeKey'] | CollapseProps['defaultActiveKey']
): string[] => {
  if (value === undefined || value === null) return []
  if (Array.isArray(value)) return value.map(String)
  return [String(value)]
}

/**
 * `React.Children.toArray` REWRITES keys (`key="more"` comes back as
 * `".$more"`), which would make `defaultActiveKey={['more']}` match nothing.
 * `Children.forEach` leaves the key as authored — the same reason
 * rc-collapse's own `getNewChild` reads `child.key` directly.
 */
const childrenToPanels = (children: React.ReactNode): PanelLike[] => {
  const panels: PanelLike[] = []
  React.Children.forEach(children, (child, index) => {
    if (!React.isValidElement(child)) return
    const element = child as React.ReactElement<PanelLike>
    panels.push({ key: element.key ?? index, ...element.props })
  })
  return panels
}

/** antd's default: `RightOutlined`, rotated a quarter turn while open. */
const DefaultExpandIcon = ({ isActive }: { isActive: boolean }) => (
  <RightOutlined className="text-xs" rotate={isActive ? 90 : 0} aria-hidden />
)

/**
 * antd keeps a panel that has been opened MOUNTED after it closes and hides it
 * with `display: none` (`.ant-collapse-content-hidden`; rc-collapse's CSSMotion
 * runs with `removeOnLeave: false`). Radix computes `hidden` as
 * `!(forceMount || open)`, so the `forceMount` that keeps the node in the DOM
 * also clears `hidden` and a closed panel would stay on screen.
 *
 * Applying `hidden` immediately instead would kill the exit animation, so it
 * is applied when that animation ENDS. Three details make it safe:
 *
 * • The un-hide is a RENDER-PHASE update, not an effect, so the element is
 *   already visible in the commit Radix measures its height in. An effect
 *   would run one commit late and Radix would measure a `display: none` box —
 *   `--radix-accordion-content-height` would be 0 and the panel would open
 *   onto nothing.
 * • If no animation is running — `prefers-reduced-motion`, or jsdom, which
 *   implements neither `getAnimations` nor animation events — it settles at
 *   once, so a test sees antd's hidden-when-closed contract rather than a
 *   panel that never shuts.
 * • `getAnimations` is read after a frame, because the animation is created at
 *   the next style recalculation rather than during the commit.
 */
const useSettledClosed = (
  isActive: boolean,
  ref: React.RefObject<HTMLDivElement>
) => {
  const [settled, setSettled] = React.useState(!isActive)
  const [previous, setPrevious] = React.useState(isActive)

  if (previous !== isActive) {
    setPrevious(isActive)
    if (isActive) setSettled(false)
  }

  React.useEffect(() => {
    if (isActive) return undefined
    const node = ref.current
    if (!node || typeof node.getAnimations !== 'function') {
      setSettled(true)
      return undefined
    }
    const frame = requestAnimationFrame(() => {
      if (node.getAnimations().length === 0) setSettled(true)
    })
    return () => cancelAnimationFrame(frame)
  }, [isActive, ref])

  // React's `onAnimationEnd` BUBBLES, so any animation finishing anywhere in a
  // panel's children would otherwise settle the panel — applying `hidden` mid
  // exit and cutting the collapse-up short. Only the content wrapper's own
  // animation is the one antd's `removeOnLeave: false` waits for.
  const onAnimationEnd = React.useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return
      if (!isActive) setSettled(true)
    },
    [isActive]
  )

  return { hidden: !isActive && settled, onAnimationEnd }
}

/* --------------------------------------------------------------------------
 * GEOMETRY, transcribed from `antd/es/collapse/style/index.js` rather than
 * re-chosen: `headerPadding` is `paddingSM padding` (12px 16px), the small and
 * large variants are `collapseHeaderPadding{SM,LG}` with their own
 * inline-start inset and a matching arrow offset, and `contentPadding` is
 * `padding 16px`. Fifteen stylesheets here override exactly these by name, so
 * moving one silently re-spaces a page nobody in this diff touched.
 * ----------------------------------------------------------------------- */
type Size = NonNullable<CollapseProps['size']>

const HEADER_PADDING: Record<Size, string> = {
  small: 'py-2 ps-2 pe-3',
  middle: 'py-3 px-4',
  large: 'py-4 ps-4 pe-6 text-base',
}

/** `-expand-icon`'s `height`, then its `marginInlineStart` arrow offset. */
const ICON_BOX: Record<Size, string> = {
  small: 'h-[22px] ms-1',
  middle: 'h-[22px]',
  large: 'h-6 ms-2',
}

const CONTENT_PADDING: Record<Size, string> = {
  small: 'p-3',
  middle: 'p-4',
  large: 'p-6',
}

/**
 * `genGhostStyle` overrides `paddingBlock` to `paddingSM` and leaves the inline
 * padding to the size rule above — so ghost keeps its size's `px` and takes a
 * flat 12px block padding at every size.
 */
const GHOST_CONTENT_PADDING: Record<Size, string> = {
  small: 'px-3 py-3',
  middle: 'px-4 py-3',
  large: 'px-6 py-3',
}

/**
 * antd's own resolution order: `showArrow === false` removes the arrow, a call
 * site's `expandIcon` replaces it, otherwise the default arrow. A falsy result
 * — `expandIcon={() => null}`, which two call sites pass — takes the wrapper
 * with it, and the wrapper carries the icon's inline padding.
 *
 * antd then CLONES whatever came back and adds `${prefixCls}-arrow` to it, so
 * `.ant-collapse-arrow` matches a call site's own icon as well as the default
 * one — and three stylesheets here reach for exactly that on a custom icon.
 * The `data-slot` twin has to be applied the same way, or those rules are live
 * in antd mode and dead in shadcn mode.
 */
const renderExpandIcon = (
  panel: PanelLike,
  isActive: boolean,
  expandIcon: CollapseProps['expandIcon']
): React.ReactNode => {
  if (panel.showArrow === false) return null
  const icon = expandIcon
    ? expandIcon({ ...panel, isActive } as Parameters<
        NonNullable<CollapseProps['expandIcon']>
      >[0])
    : DefaultExpandIcon({ isActive })
  if (!React.isValidElement(icon)) return icon
  return React.cloneElement(icon as React.ReactElement, {
    'data-slot': 'collapse-arrow',
  })
}

const CollapsePanelRow = ({
  panelKey,
  isActive,
  disabled,
  label,
  icon,
  iconPosition,
  extra,
  panel,
  size,
  ghost,
  mounted,
  destroyInactivePanel,
}: {
  panelKey: string
  isActive: boolean
  disabled: boolean
  label: React.ReactNode
  icon: React.ReactNode
  iconPosition: 'start' | 'end'
  extra: React.ReactNode
  panel: PanelLike
  size: Size
  ghost: boolean
  mounted: boolean
  destroyInactivePanel: boolean
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const { hidden, onAnimationEnd } = useSettledClosed(isActive, contentRef)

  const iconNode = icon ? (
    <span
      data-slot="collapse-expand-icon"
      className={cn(
        'flex shrink-0 items-center',
        ICON_BOX[size],
        iconPosition === 'end' ? 'ps-3' : 'pe-3'
      )}
    >
      {icon}
    </span>
  ) : null

  return (
    <AccordionItem
      {...panelRestProps(panel)}
      value={panelKey}
      disabled={disabled}
      data-slot="collapse-item"
      className={cn(!ghost && 'border-b border-border', panel.className)}
      style={panel.style}
    >
      <AccordionHeader
        data-slot="collapse-header"
        className={cn(
          'flex-nowrap items-start',
          HEADER_PADDING[size],
          panel.headerClass
        )}
      >
        <AccordionTrigger
          data-slot="collapse-header-trigger"
          className="min-w-0 items-start"
        >
          {iconPosition === 'start' && iconNode}
          <span
            data-slot="collapse-header-text"
            className="me-auto min-w-0 flex-auto"
          >
            {label}
          </span>
          {iconPosition === 'end' && iconNode}
        </AccordionTrigger>
        {extra !== undefined &&
          extra !== null &&
          typeof extra !== 'boolean' && (
            <span data-slot="collapse-extra" className="shrink-0">
              {extra}
            </span>
          )}
      </AccordionHeader>
      <AccordionContent
        ref={contentRef}
        data-slot="collapse-content"
        // `forceMount` is what keeps an opened panel in the DOM after it
        // closes; a never-opened panel stays absent, matching rc-collapse's
        // lazy first render. `destroyInactivePanel` turns the whole thing off,
        // which is antd's `removeOnLeave`.
        forceMount={!destroyInactivePanel && mounted ? true : undefined}
        hidden={hidden}
        onAnimationEnd={onAnimationEnd}
        className={cn(!ghost && 'border-t border-border bg-background')}
      >
        <div
          data-slot="collapse-content-box"
          className={
            ghost ? GHOST_CONTENT_PADDING[size] : CONTENT_PADDING[size]
          }
        >
          {panel.children}
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

const CollapseRoot = ({
  items,
  children,
  activeKey,
  defaultActiveKey,
  onChange,
  accordion = false,
  bordered = true,
  ghost = false,
  size = 'middle',
  collapsible,
  expandIcon,
  expandIconPosition = 'start',
  destroyInactivePanel = false,
  className,
  style,
  // antd-only knobs with no Radix equivalent and no call site here. Named so
  // they cannot reach the DOM as unknown attributes if one ever appears.
  prefixCls: _prefixCls,
  rootClassName: _rootClassName,
  ...rest
}: CollapseProps) => {
  const panels: PanelLike[] = items
    ? (items as PanelLike[])
    : childrenToPanels(children)

  const controlled = activeKey !== undefined
  const [innerKeys, setInnerKeys] = React.useState<string[]>(() =>
    toKeyArray(defaultActiveKey)
  )
  const openKeys = controlled ? toKeyArray(activeKey) : innerKeys

  // rc-collapse renders nothing for a panel that has never been active, then
  // keeps the node mounted once it has been. Radix would unmount it, so the set
  // of panels that have ever been open is tracked and those are rendered with
  // `forceMount` from then on.
  //
  // Fed from the EFFECTIVE open set during render, never from what `onChange`
  // proposed: a controlled parent is free to refuse a key, and antd mounts
  // nothing when it does. Recorded in a ref rather than state because the set
  // has to be right in the SAME render the key opens in — a state write would
  // land a commit later, after Radix has already measured the panel.
  //
  // Reconciled with the panels actually rendered FIRST: a call site is free to
  // re-key its `items` (`generate-export/Filters` keys off the export type,
  // `PropertyCollections` off dates), and a brand-new panel that happens to
  // reuse a dead panel's key is not one antd would have mounted — it would
  // inherit `forceMount` and eagerly render children nobody expanded.
  const panelKeys = panels.map((panel, index) => String(panel.key ?? index))
  const everOpenRef = React.useRef<Set<string>>(new Set())
  const liveKeys = new Set(panelKeys)
  everOpenRef.current.forEach((key) => {
    if (!liveKeys.has(key)) everOpenRef.current.delete(key)
  })
  openKeys.forEach((key) => {
    if (liveKeys.has(key)) everOpenRef.current.add(key)
  })

  const emit = (next: string[]) => {
    if (!controlled) setInnerKeys(next)
    // Always an array, in both modes — see `toKeyArray` above.
    onChange?.(next)
  }

  // antd 5 still accepts the deprecated `left`/`right` spellings and maps them.
  const iconPosition =
    expandIconPosition === 'right' || expandIconPosition === 'end'
      ? 'end'
      : 'start'

  const body = panels.map((panel, index) => {
    const key = panelKeys[index]
    const isActive = openKeys.includes(key)
    const panelCollapsible = panel.collapsible ?? collapsible
    const disabled = panelCollapsible === 'disabled'
    const label = panel.label ?? panel.header
    const mounted = Boolean(panel.forceRender) || everOpenRef.current.has(key)

    // rc-collapse: `showArrow && iconNode`, and it wraps only a TRUTHY icon.
    // `expandIcon={() => null}` therefore removes the wrapper's padding too.
    const rawIcon = renderExpandIcon(panel, isActive, expandIcon)

    return (
      <CollapsePanelRow
        key={key}
        panelKey={key}
        isActive={isActive}
        disabled={disabled}
        label={label}
        icon={rawIcon}
        iconPosition={iconPosition}
        extra={panel.extra}
        panel={panel}
        size={size}
        ghost={ghost}
        mounted={mounted}
        destroyInactivePanel={destroyInactivePanel}
      />
    )
  })

  const rootProps = {
    'data-slot': 'collapse',
    'data-ghost': ghost ? 'true' : undefined,
    'data-bordered': !ghost && bordered ? 'true' : undefined,
    'data-size': size,
    'data-icon-position': iconPosition,
    className: cn(
      'text-foreground',
      !ghost && 'bg-muted/50',
      !ghost &&
        bordered &&
        'overflow-hidden rounded-lg border border-b-0 border-border',
      className
    ),
    style,
    ...rest,
  }

  if (accordion) {
    return (
      <Accordion
        type="single"
        collapsible
        value={openKeys[0] ?? ''}
        onValueChange={(value) => emit(value ? [value] : [])}
        {...rootProps}
      >
        {body}
      </Accordion>
    )
  }

  return (
    <Accordion
      type="multiple"
      value={openKeys}
      onValueChange={emit}
      {...rootProps}
    >
      {body}
    </Accordion>
  )
}

CollapseRoot.displayName = 'Collapse'

export const Collapse = CollapseRoot

export type { CollapseProps, CollapsePanelProps }
